/** Plantillas listas para insertar (equivalente a la galería de plantillas de Whiteboard). */
import type { Shape } from '../../shared/types.ts'
import { createGeo, createNote, createText } from '../state/shapes.ts'

export interface Template {
  id: string
  name: string
  description: string
  build(originX: number, originY: number, createdBy: string, startZ: number): Shape[]
}

interface Ctx {
  x: number
  y: number
  by: string
  z: number
}

function heading(ctx: Ctx, text: string, dx: number, dy: number, size = 40): Shape {
  const shape = createText({
    x: ctx.x + dx,
    y: ctx.y + dy,
    color: '#1A1A1A',
    fontSize: size,
    createdBy: ctx.by,
    z: ctx.z++,
  })
  shape.text = text
  shape.w = 620
  shape.h = size * 1.3
  return shape
}

function column(ctx: Ctx, title: string, dx: number, dy: number, color: string, w = 300, hgt = 620): Shape[] {
  const frame = createGeo({
    kind: 'roundRectangle',
    x: ctx.x + dx,
    y: ctx.y + dy,
    w,
    h: hgt,
    color: '#C8C8C8',
    fill: '#FFFFFF',
    strokeWidth: 2,
    createdBy: ctx.by,
    z: ctx.z++,
  })
  const label = createGeo({
    kind: 'roundRectangle',
    x: ctx.x + dx,
    y: ctx.y + dy,
    w,
    h: 56,
    color: color,
    fill: color,
    strokeWidth: 2,
    createdBy: ctx.by,
    z: ctx.z++,
  })
  label.text = title
  label.fontSize = 22
  label.textColor = '#1A1A1A'
  return [frame, label]
}

function note(ctx: Ctx, text: string, dx: number, dy: number, color: string, size = 160): Shape {
  const n = createNote({
    x: ctx.x + dx,
    y: ctx.y + dy,
    color,
    createdBy: ctx.by,
    z: ctx.z++,
    size,
  })
  n.text = text
  return n
}

export const TEMPLATES: Template[] = [
  {
    id: 'brainstorm',
    name: 'Lluvia de ideas',
    description: 'Cuadrícula de notas para llenar en equipo',
    build(x, y, by, z) {
      const ctx: Ctx = { x, y, by, z }
      const out: Shape[] = [heading(ctx, 'Lluvia de ideas', 0, -90)]
      const colors = ['#FEF3A3', '#C9E7FF', '#D6F0BE', '#FFD1E3']
      for (let row = 0; row < 3; row++) {
        for (let col = 0; col < 4; col++) {
          out.push(note(ctx, '', col * 180, row * 180, colors[col], 160))
        }
      }
      return out
    },
  },
  {
    id: 'kanban',
    name: 'Tablero Kanban',
    description: 'Por hacer · En curso · Hecho',
    build(x, y, by, z) {
      const ctx: Ctx = { x, y, by, z }
      const out: Shape[] = [heading(ctx, 'Tablero de trabajo', 0, -90)]
      const cols: [string, string][] = [
        ['Por hacer', '#FFD1E3'],
        ['En curso', '#C9E7FF'],
        ['Hecho', '#D6F0BE'],
      ]
      cols.forEach(([title, color], i) => {
        out.push(...column(ctx, title, i * 340, 0, color))
        for (let n = 0; n < 2; n++) out.push(note(ctx, '', i * 340 + 20, 80 + n * 150, color, 140))
      })
      return out
    },
  },
  {
    id: 'retro',
    name: 'Retrospectiva',
    description: 'Qué funcionó, qué mejorar, acciones',
    build(x, y, by, z) {
      const ctx: Ctx = { x, y, by, z }
      const out: Shape[] = [heading(ctx, 'Retrospectiva', 0, -90)]
      const cols: [string, string][] = [
        ['✅ Qué funcionó', '#D6F0BE'],
        ['⚠️ Qué mejorar', '#FEF3A3'],
        ['🎯 Acciones', '#C9E7FF'],
      ]
      cols.forEach(([title, color], i) => {
        out.push(...column(ctx, title, i * 340, 0, color))
        out.push(note(ctx, '', i * 340 + 20, 80, color, 140))
      })
      return out
    },
  },
  {
    id: 'standup',
    name: 'Reunión diaria',
    description: 'Ayer · Hoy · Bloqueos',
    build(x, y, by, z) {
      const ctx: Ctx = { x, y, by, z }
      const out: Shape[] = [heading(ctx, 'Reunión diaria', 0, -90)]
      const cols: [string, string][] = [
        ['Ayer', '#E0D5FA'],
        ['Hoy', '#C3F0E7'],
        ['Bloqueos', '#FFD8A8'],
      ]
      cols.forEach(([title, color], i) => {
        out.push(...column(ctx, title, i * 340, 0, color, 300, 480))
        out.push(note(ctx, '', i * 340 + 20, 80, color, 140))
      })
      return out
    },
  },
  {
    id: 'matrix',
    name: 'Matriz impacto/esfuerzo',
    description: 'Prioriza ideas en 4 cuadrantes',
    build(x, y, by, z) {
      const ctx: Ctx = { x, y, by, z }
      const out: Shape[] = [heading(ctx, 'Impacto vs. esfuerzo', 0, -90)]
      const size = 640
      out.push(
        createGeo({
          kind: 'rectangle',
          x: ctx.x,
          y: ctx.y,
          w: size,
          h: size,
          color: '#B0B0B0',
          fill: '#FFFFFF',
          strokeWidth: 2,
          createdBy: by,
          z: ctx.z++,
        }),
      )
      const vertical = createGeo({
        kind: 'line',
        x: ctx.x + size / 2,
        y: ctx.y,
        w: 0,
        h: size,
        color: '#B0B0B0',
        fill: 'none',
        strokeWidth: 2,
        createdBy: by,
        z: ctx.z++,
      })
      const horizontal = createGeo({
        kind: 'line',
        x: ctx.x,
        y: ctx.y + size / 2,
        w: size,
        h: 0,
        color: '#B0B0B0',
        fill: 'none',
        strokeWidth: 2,
        createdBy: by,
        z: ctx.z++,
      })
      out.push(vertical, horizontal)
      const labels: [string, number, number][] = [
        ['Ganancias rápidas', 40, 20],
        ['Proyectos grandes', size / 2 + 40, 20],
        ['Rellenos', 40, size - 60],
        ['Ingratos', size / 2 + 40, size - 60],
      ]
      for (const [text, dx, dy] of labels) {
        const t = createText({
          x: ctx.x + dx,
          y: ctx.y + dy,
          color: '#616161',
          fontSize: 22,
          createdBy: by,
          z: ctx.z++,
        })
        t.text = text
        t.w = 260
        t.h = 30
        out.push(t)
      }
      return out
    },
  },
  {
    id: 'week',
    name: 'Plan semanal',
    description: 'Lunes a viernes en columnas',
    build(x, y, by, z) {
      const ctx: Ctx = { x, y, by, z }
      const out: Shape[] = [heading(ctx, 'Plan de la semana', 0, -90)]
      const days = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes']
      days.forEach((day, i) => {
        out.push(...column(ctx, day, i * 260, 0, '#C9E7FF', 240, 560))
      })
      return out
    },
  },
]
